import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast, { Toaster } from 'react-hot-toast';
import { getPublicPayment, submitPublicProof } from '../../services/feeService';
import { isMethodEnabled } from '../../utils/paymentMethods';
import { ktmToday } from '../../utils/calendar';
import {
  MdCheckCircle, MdHourglassTop, MdWarning, MdUpload,
  MdQrCode2, MdAccountBalance, MdClose, MdFilePresent,
} from 'react-icons/md';

const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString()}`;

const METHOD_OPTS = [
  { value: 'esewa',   label: 'eSewa'         },
  { value: 'khalti',  label: 'Khalti'        },
  { value: 'fonepay', label: 'Fonepay'       },
  { value: 'bank',    label: 'Bank Transfer' },
  { value: 'cash',    label: 'Cash'          },
  { value: 'other',   label: 'Other'         },
];

const PROOF_ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_SIZE = 5 * 1024 * 1024;

function QrCard({ label, id, qr, color }) {
  if (!id && !qr) return null;
  return (
    <div className="border border-divider rounded-2xl p-5 flex flex-col items-center gap-3">
      <div className="flex items-center gap-2">
        <MdQrCode2 size={20} style={{ color }} />
        <span className="font-semibold text-secondary text-base">{label}</span>
      </div>
      {id && (
        <p className="text-sm text-muted font-mono bg-nav-hover px-4 py-2 rounded-xl select-all w-full text-center break-all">
          {id}
        </p>
      )}
      {qr?.url && (
        <img
          src={qr.url}
          alt={`${label} QR`}
          className="w-full max-w-[200px] h-auto aspect-square object-contain border border-divider rounded-xl mx-auto"
        />
      )}
    </div>
  );
}

export default function ParentPayment() {
  const { token } = useParams();
  const fileRef   = useRef();
  const [method,    setMethod]    = useState('esewa');
  const [amount,    setAmount]    = useState('');
  const [paidDate,  setPaidDate]  = useState(ktmToday());
  const [reference, setReference] = useState('');
  const [file,      setFile]      = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-payment', token],
    queryFn:  () => getPublicPayment(token).then(r => r.data),
    retry: false,
  });

  // If the club disabled the default method, fall back to the first enabled one.
  useEffect(() => {
    const acc = data?.paymentSettings?.acceptedPaymentMethods || [];
    if (acc.length === 0) return;
    if (!isMethodEnabled(method, acc)) {
      const first = ['esewa', 'khalti', 'fonepay', 'bank', 'cash', 'other'].find(m => isMethodEnabled(m, acc));
      if (first && first !== method) setMethod(first);
    }
  }, [data, method]);

  const mutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('method', method);
      if (amount)    fd.append('amount',    amount);
      if (paidDate)  fd.append('paidDate',  paidDate);
      if (reference) fd.append('reference', reference);
      if (file)      fd.append('proofFile', file);
      return submitPublicProof(token, fd);
    },
    onSuccess: () => setSubmitted(true),
    onError:   (e) => toast.error(e.response?.data?.message || 'Submission failed. Please try again.'),
  });

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!PROOF_ALLOWED.includes(f.type)) {
      toast.error('Only JPEG, PNG, WEBP, or PDF allowed');
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error('File must be under 5MB');
      return;
    }
    setFile(f);
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-subtle">Loading payment details…</p>
        </div>
      </div>
    );
  }

  // ── Error / not found ────────────────────────────────────────────────────────
  if (error || !data?.success) {
    const msg = error?.response?.data?.message || 'Payment link not found or has expired.';
    const alreadyPaid = error?.response?.data?.alreadyPaid;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="bg-card rounded-2xl shadow-sm border border-divider p-8 max-w-sm w-full text-center">
          {alreadyPaid
            ? <MdCheckCircle size={52} className="mx-auto text-green-500 mb-4" />
            : <MdWarning size={52} className="mx-auto text-red-400 mb-4" />
          }
          <p className="text-secondary text-base leading-relaxed">{msg}</p>
        </div>
      </div>
    );
  }

  const { payment, student, club, paymentSettings: ps } = data;
  const brandColor = club.primaryColor || '#E24B4A';
  const accepted   = ps.acceptedPaymentMethods || []; // methods the club enabled (empty → all)
  const methodOpts = METHOD_OPTS.filter(o => isMethodEnabled(o.value, accepted));

  // ── Already proof_submitted ──────────────────────────────────────────────────
  const isWaiting  = payment.status === 'proof_submitted' && !submitted;
  const wasRejected = payment.proofRejectedAt && payment.status !== 'proof_submitted';

  // ── Success screen ───────────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="bg-card rounded-2xl shadow-sm border border-divider p-8 max-w-sm w-full text-center">
          <MdCheckCircle size={64} className="mx-auto mb-5" style={{ color: brandColor }} />
          <h2 className="text-xl font-bold text-secondary mb-2">Proof Submitted!</h2>
          <p className="text-muted text-sm leading-relaxed">
            Your payment screenshot has been sent to <strong>{club.name}</strong>. They will review and confirm it shortly.
          </p>
        </div>
      </div>
    );
  }

  // ── Already waiting for review ───────────────────────────────────────────────
  if (isWaiting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="bg-card rounded-2xl shadow-sm border border-divider p-8 max-w-sm w-full text-center">
          <MdHourglassTop size={52} className="mx-auto mb-4 text-blue-500" />
          <h2 className="text-xl font-bold text-secondary mb-2">Under Review</h2>
          <p className="text-muted text-sm leading-relaxed">
            Payment proof for <strong>{student.firstName}</strong> ({payment.periodLabel}) has been submitted and is being reviewed by <strong>{club.name}</strong>.
          </p>
        </div>
      </div>
    );
  }

  const hasQrs = ps.esewaQr || ps.khaltiQr || ps.fonepayQr || ps.bankQr;
  const hasIds = ps.esewaId || ps.khaltiId || ps.fonepayId || ps.bankAccountNumber;

  return (
    <div className="min-h-screen bg-background py-6 px-4 pb-safe">
      <Toaster position="top-center" toastOptions={{ duration: 3000, style: { fontSize: '0.875rem' } }} />
      <div className="max-w-lg mx-auto space-y-4">

        {/* Header card — club + fee summary */}
        <div className="bg-card rounded-2xl shadow-sm border border-divider p-5">
          <div className="flex items-center gap-3 mb-5">
            {club.logo
              ? <img src={club.logo} alt={club.name} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
              : (
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
                  style={{ background: brandColor }}
                >
                  {club.name[0]}
                </div>
              )
            }
            <div>
              <p className="text-xs text-subtle uppercase tracking-wide font-medium">Fee Payment</p>
              <h1 className="font-bold text-secondary text-lg leading-tight">{club.name}</h1>
            </div>
          </div>

          {/* Amount — prominent display */}
          <div className="rounded-2xl p-4 mb-4 text-center" style={{ background: `${brandColor}10`, border: `1px solid ${brandColor}25` }}>
            <p className="text-xs text-muted font-medium uppercase tracking-wide mb-1">Amount to Pay</p>
            <p className="text-3xl font-bold" style={{ color: brandColor }}>{fmtNPR(payment.amount)}</p>
            <p className="text-sm text-muted mt-1">{payment.periodLabel}</p>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm py-1 border-b border-divider">
              <span className="text-muted">Student</span>
              <span className="font-semibold text-secondary">{student.firstName}</span>
            </div>
            <div className="flex justify-between text-sm py-1">
              <span className="text-muted">Status</span>
              <span className="font-semibold text-amber-600 capitalize">{payment.status?.replace(/_/g, ' ')}</span>
            </div>
          </div>

          {wasRejected && (
            <div className="mt-4 flex gap-3 items-start bg-red-50 border border-red-200 rounded-xl p-4">
              <MdWarning size={20} className="text-red-500 mt-0.5 shrink-0" />
              <div className="text-sm text-red-700">
                <p className="font-semibold">Previous submission not accepted</p>
                {payment.proofRejectionReason && (
                  <p className="mt-1 text-red-600">{payment.proofRejectionReason}</p>
                )}
                <p className="mt-1 text-red-500 text-xs">Please upload your proof again below.</p>
              </div>
            </div>
          )}
        </div>

        {/* Payment methods — QR codes */}
        {(hasQrs || hasIds || ps.paymentInstructions) ? (
          <div className="bg-card rounded-2xl shadow-sm border border-divider p-5">
            <h2 className="font-semibold text-secondary mb-4 flex items-center gap-2 text-base">
              <MdQrCode2 size={20} style={{ color: brandColor }} /> How to Pay
            </h2>

            {(hasQrs || hasIds) && (
              /* Single column on mobile, 2 columns on tablet+ */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {isMethodEnabled('esewa', accepted)   && <QrCard label="eSewa"   id={ps.esewaId}   qr={ps.esewaQr}   color="#60BB46" />}
                {isMethodEnabled('khalti', accepted)  && <QrCard label="Khalti"  id={ps.khaltiId}  qr={ps.khaltiQr}  color="#5C2D91" />}
                {isMethodEnabled('fonepay', accepted) && <QrCard label="Fonepay" id={ps.fonepayId} qr={ps.fonepayQr} color="#E24B4A" />}
                {isMethodEnabled('bank', accepted) && (ps.bankAccountNumber || ps.bankQr) && (
                  <div className="border border-divider rounded-2xl p-5 flex flex-col items-center gap-3">
                    <div className="flex items-center gap-2">
                      <MdAccountBalance size={20} className="text-muted" />
                      <span className="font-semibold text-secondary text-base">Bank</span>
                    </div>
                    {ps.bankName && <p className="text-xs text-muted">{ps.bankName}</p>}
                    {ps.bankAccountName && <p className="text-sm text-muted font-medium">{ps.bankAccountName}</p>}
                    {ps.bankAccountNumber && (
                      <p className="text-sm font-mono bg-nav-hover px-4 py-2 rounded-xl select-all w-full text-center">
                        {ps.bankAccountNumber}
                      </p>
                    )}
                    {ps.bankQr?.url && (
                      <img
                        src={ps.bankQr.url}
                        alt="Bank QR"
                        className="w-full max-w-[200px] h-auto aspect-square object-contain border border-divider rounded-xl mx-auto"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {ps.paymentInstructions && (
              <p className="mt-4 text-sm text-muted bg-nav-hover rounded-xl p-4 whitespace-pre-line leading-relaxed">{ps.paymentInstructions}</p>
            )}
          </div>
        ) : (
          <div className="bg-card rounded-2xl border border-divider p-6 text-center">
            <MdAccountBalance size={40} className="mx-auto text-subtle mb-3" />
            <p className="text-secondary font-medium">Payment details coming soon</p>
            <p className="text-muted text-sm mt-1">This club hasn't added payment methods yet. Please contact your club directly to arrange payment.</p>
            {(club.contactEmail || club.contactPhone) && (
              <p className="mt-3 text-sm">
                {club.contactEmail && <a href={`mailto:${club.contactEmail}`} className="font-medium hover:underline break-all" style={{ color: brandColor }}>{club.contactEmail}</a>}
                {club.contactEmail && club.contactPhone && <span className="text-subtle"> · </span>}
                {club.contactPhone && <a href={`tel:${club.contactPhone}`} className="font-medium hover:underline" style={{ color: brandColor }}>{club.contactPhone}</a>}
              </p>
            )}
          </div>
        )}

        {/* Proof upload form */}
        <div className="bg-card rounded-2xl shadow-sm border border-divider p-5">
          <h2 className="font-semibold text-secondary mb-5 flex items-center gap-2 text-base">
            <MdUpload size={20} style={{ color: brandColor }} />
            Submit Payment Proof
          </h2>

          <div className="space-y-4">
            {/* Payment method — pill buttons */}
            <div>
              <label className="block text-xs font-semibold text-muted mb-2 uppercase tracking-wide">Payment Method</label>
              <div className="grid grid-cols-3 gap-2">
                {methodOpts.map(o => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => setMethod(o.value)}
                    className={`py-2.5 px-2 rounded-xl text-xs font-semibold border transition-colors ${
                      method === o.value
                        ? 'text-white border-transparent'
                        : 'bg-nav-hover text-muted border-divider hover:border-subtle'
                    }`}
                    style={method === o.value ? { background: brandColor, borderColor: brandColor } : {}}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount + Date — stacked on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wide">Amount Paid (NPR)</label>
                <input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder={String(payment.amount)}
                  inputMode="numeric"
                  className="w-full border border-divider rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-300"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wide">Payment Date</label>
                <input
                  type="date"
                  value={paidDate}
                  onChange={e => setPaidDate(e.target.value)}
                  className="w-full border border-divider rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-300"
                />
              </div>
            </div>

            {/* Reference */}
            <div>
              <label className="block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wide">
                Transaction ID (optional)
              </label>
              <input
                type="text"
                value={reference}
                onChange={e => setReference(e.target.value)}
                placeholder="e.g. TXN-123456"
                className="w-full border border-divider rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-300"
              />
            </div>

            {/* File upload — tall, easy to tap */}
            <div>
              <label className="block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wide">
                Payment Screenshot <span className="text-red-500">*</span>
              </label>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-divider rounded-2xl p-6 text-center cursor-pointer hover:border-red-300 hover:bg-red-50 active:bg-red-50 transition-colors"
                style={{ minHeight: '100px' }}
              >
                {file ? (
                  <div className="flex items-center justify-center gap-3 text-sm text-secondary">
                    <MdFilePresent size={24} className="text-green-500 flex-shrink-0" />
                    <span className="truncate max-w-[200px] font-medium">{file.name}</span>
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                      className="text-subtle hover:text-red-500 flex-shrink-0 p-1"
                    >
                      <MdClose size={18} />
                    </button>
                  </div>
                ) : (
                  <div className="text-subtle">
                    <MdUpload size={32} className="mx-auto mb-2 text-subtle" />
                    <p className="text-sm font-medium text-muted">Tap to upload screenshot</p>
                    <p className="text-xs mt-1 text-subtle">JPEG, PNG, or PDF · max 5 MB</p>
                  </div>
                )}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                capture="environment"
                className="hidden"
                onChange={handleFile}
              />
            </div>

            {/* Submit — large, full-width, easy to tap. Proof file is required. */}
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !file}
              className="w-full py-4 rounded-2xl font-bold text-white text-base transition-opacity disabled:opacity-60 disabled:cursor-not-allowed active:opacity-80"
              style={{ background: brandColor }}
            >
              {mutation.isPending ? 'Submitting…' : 'Submit Payment Proof'}
            </button>
            {!file && (
              <p className="text-center text-xs text-subtle">Upload your payment screenshot to submit.</p>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-subtle pb-6">
          Powered by TrainAxis · Your payment details are secure
        </p>
      </div>
    </div>
  );
}
